import type { FC, ReactNode } from 'react';
import { Icon } from '@iconify/react';
import type { IconifyIcon } from '@iconify/react';
import {
  SnackbarProvider as RawSnackbarProvider,
  MaterialDesignContent,
  type SnackbarProviderProps
} from 'notistack';
import infoFill from '@iconify/icons-eva/info-fill';
import alertCircleFill from '@iconify/icons-eva/alert-circle-fill';
import alertTriangleFill from '@iconify/icons-eva/alert-triangle-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import { styled, alpha } from '@mui/material/styles';
import { Box, GlobalStyles } from '@mui/material';

type SnackbarIconColor = 'info' | 'success' | 'warning' | 'error';

type SnackbarIconProps = {
  icon: IconifyIcon;
  color: SnackbarIconColor;
};

const SnackbarProvider = RawSnackbarProvider as unknown as FC<SnackbarProviderProps>;

function SnackbarIcon({ icon, color }: SnackbarIconProps) {
  return (
    <Box
      component="span"
      sx={{
        mr: 1.5,
        width: 40,
        height: 40,
        display: 'flex',
        borderRadius: 1.5,
        alignItems: 'center',
        justifyContent: 'center',
        color: `${color}.main`,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.16)
      }}
    >
      <Icon icon={icon} width={24} height={24} />
    </Box>
  );
}

const StyledContent = styled(MaterialDesignContent)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(1.5),
  margin: theme.spacing(0.25, 0),
  boxShadow: theme.customShadows.z8,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  '#notistack-snackbar': {
    padding: 0,
    fontWeight: theme.typography.fontWeightMedium
  },
  '#notistack-snackbar + div': {
    marginRight: -4,
    '& svg': {
      width: 20,
      height: 20,
      opacity: 0.48,
      '&:hover': { opacity: 1 }
    }
  }
}));

export default function NotistackProvider({ children }: { children: ReactNode }) {
  return (
    <>
      <GlobalStyles
        styles={{
          '.notistack-SnackbarContainer': { pointerEvents: 'unset' },
          '.notistack-CollapseWrapper': { width: '100%' }
        }}
      />
      <SnackbarProvider
        dense
        maxSnack={5}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        iconVariant={{
          success: <SnackbarIcon icon={checkmarkCircle2Fill} color="success" />,
          error: <SnackbarIcon icon={infoFill} color="error" />,
          warning: <SnackbarIcon icon={alertTriangleFill} color="warning" />,
          info: <SnackbarIcon icon={alertCircleFill} color="info" />
        }}
        Components={{
          default: StyledContent,
          success: StyledContent,
          error: StyledContent,
          warning: StyledContent,
          info: StyledContent
        }}
      >
        {children}
      </SnackbarProvider>
    </>
  );
}
